/**
 * @file Modal.js
 * @description Declarative component for Bootstrap modal using provided JSX content.
 */
import React, { useContext } from 'react';
import { ModalContext, ModalTypes } from '../../../store/Contexts';
import AcknowledgementModalStyleA from './AcknowledmentModalStyleA';
import ActionModalStyleA from './ActionModalStyleA';
import DeleteConfirmation from './DeleteActionConfirmation';
import ProfileModal from './ProfileModal';
import SecurityModal from './SecurityModal';
import CommunicationsModal from './CommunicationsModal';
import FAQModal from './FAQModal';
import PrivacyModal from './PrivacyModal';
import SupportModal from './SupportModal';
import InstallationModal from './InstallationModal';
import AddFromAssetsModal from './AddFromAssetsModal';
import UploadFromDeviceModal from './UploadFromDeviceModal';
import AssetViewModal from './AssetViewModal';
import AdditionalUserFormModal from './AdditionalUserFormModal';
import MessageViewModal from './MessageViewModal';
import ImportMapTestModal from './ImportMapTestModal';
import AddServiceModal from './AddServiceModal';

/**
 * @typedef {Object} ModalConfig
 * @property {ModalTypes} type The type of the modal.
 * @property {string} title The title text of the modal.
 * @property {string} subtitle The subtitle text of the modal.
 * @property {string} text The main content text of the modal.
 * @property {function} onYes The callback to trigger when the 'Yes' button is clicked.
 * @property {function} onNo The callback to trigger when the 'No' button is clicked.
 * @property {string} yesButtonText The display text of the 'Yes' button.
 * @property {string} noButtonText The display text of the 'No' button.
 * @property {string} [userId] The user ID for additional user form modal.
 * @property {function} [onSuccess] Callback when form action succeeds.
 * @property {string} [id] Unique identifier for the modal instance.
 * @property {Object} [data] data to be passed to the modal component for rendering of content
 */

/**
 * Renders a single modal based on configuration.
 * @param {ModalConfig} modalConfig - The modal configuration.
 * @param {Function} hideModal - Function to hide the modal.
 * @param {number} zIndex - The z-index for the modal.
 * @returns {React.ReactElement} The modal component.
 */
const renderSingleModal = (modalConfig, hideModal, zIndex) => {
    const {
        type,
        title,
        subtitle,
        text,
        onYes,
        onNo,
        yesButtonText,
        noButtonText,
        acceptedFileTypes,
        assetTypes,
        wasCreatedByMe,
        requirePopulationGroupSelectionForImages,
        fileType,
        download,
        userId,
        onSuccess,
        data,
    } = modalConfig;

    /**
     * Props to be passed to the modal component based on ModalConfig.
     * @type {Object}
     */
    const modalProps = {
        title,
        subtitle,
        text,
        onYes,
        onNo,
        onHide: hideModal,
        yesButtonText: yesButtonText,
        noButtonText: noButtonText,
        acceptedFileTypes: acceptedFileTypes,
        assetTypes,
        wasCreatedByMe,
        requirePopulationGroupSelectionForImages,
        fileType,
        download,
        userId,
        onSuccess,
        zIndex,
        data,
    };

    switch (type) {
        case ModalTypes.ACTION:
            return <ActionModalStyleA {...modalProps} />;
        case ModalTypes.DELETE_CONFIRMATION:
            return <DeleteConfirmation {...modalProps} />;
        case ModalTypes.PROFILE:
            return <ProfileModal {...modalProps} />;
        case ModalTypes.SECURITY:
            return <SecurityModal {...modalProps} />;
        case ModalTypes.COMMUNICATIONS:
            return <CommunicationsModal {...modalProps} />;
        case ModalTypes.FAQ:
            return <FAQModal {...modalProps} />;
        case ModalTypes.PRIVACY:
            return <PrivacyModal {...modalProps} />;
        case ModalTypes.SUPPORT:
            return <SupportModal {...modalProps} />;
        case ModalTypes.INSTALLATION:
            return <InstallationModal {...modalProps} />;
        case ModalTypes.ADD_FROM_ASSETS:
            return <AddFromAssetsModal {...modalProps} />;
        case ModalTypes.UPLOAD_FROM_DEVICE:
            return <UploadFromDeviceModal {...modalProps} />;
        case ModalTypes.ASSET_VIEW:
            return <AssetViewModal {...modalProps} />;
        case ModalTypes.ADDITIONAL_USER_FORM:
            return <AdditionalUserFormModal {...modalProps} />;
        case ModalTypes.MESSAGE_VIEW:
            return <MessageViewModal {...modalProps} />;
        case ModalTypes.IMPORT_MAP_TEST:
            return <ImportMapTestModal {...modalProps} />;
        case ModalTypes.ADD_SERVICE:
            return <AddServiceModal {...modalProps} />;
        case ModalTypes.DEFAULT:
        default:
            return <AcknowledgementModalStyleA {...modalProps} />;
    }
};

/**
 * Modal component that renders modals based on the provided configuration.
 * @returns {React.ReactElement|null} The modal component or null if no configuration is provided.
 */
export const Modal = () => {
    const { modalStack, hideModal } = useContext(ModalContext);

    if (!modalStack || modalStack.length === 0) {
        return null;
    }

    // Base z-index for modals
    const BASE_Z_INDEX = 1040;

    return (
        <>
            {modalStack.map((modalConfig, index) => {
                // Calculate z-index: each modal in the stack gets a higher z-index
                const zIndex = BASE_Z_INDEX + index * 10;

                return (
                    <React.Fragment key={modalConfig.id}>
                        {renderSingleModal(modalConfig, hideModal, zIndex)}
                    </React.Fragment>
                );
            })}
        </>
    );
};
